import React from 'react';
import { useTranslation } from 'react-i18next';

export interface FilterSortOptions {
  sortBy: 'date-desc' | 'date-asc' | 'title-asc' | 'title-desc' | 'updated-desc' | 'updated-asc';
  filterTag: string;
}

interface FilterSortControlsProps {
  options: FilterSortOptions;
  onOptionsChange: (options: FilterSortOptions) => void;
  availableTags: string[];
}

const FilterSortControls: React.FC<FilterSortControlsProps> = ({
  options,
  onOptionsChange,
  availableTags,
}) => {
  const { t } = useTranslation();

  const handleSortChange = (sortBy: FilterSortOptions['sortBy']) => {
    onOptionsChange({ ...options, sortBy });
  };

  const handleTagFilterChange = (filterTag: string) => {
    onOptionsChange({ ...options, filterTag });
  };

  return (
    <div className="flex flex-col sm:flex-row gap-4 mb-6 p-4 glass-card rounded-2xl shadow-large">
      {/* Sort Controls */}
      <div className="flex-1">
        <label className="block text-sm font-medium text-neutral-600 dark:text-neutral-400 mb-2">
          {t('filterSort.sortBy')}
        </label>
        <div className="relative">
          <select
            value={options.sortBy}
            onChange={(e) => handleSortChange(e.target.value as FilterSortOptions['sortBy'])}
            className="w-full px-4 py-3 pr-10 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 appearance-none"
          >
            <option value="date-desc">{t('filterSort.dateDesc')}</option>
            <option value="date-asc">{t('filterSort.dateAsc')}</option>
            <option value="updated-desc">{t('filterSort.updatedDesc')}</option>
            <option value="updated-asc">{t('filterSort.updatedAsc')}</option>
            <option value="title-asc">{t('filterSort.titleAsc')}</option>
            <option value="title-desc">{t('filterSort.titleDesc')}</option>
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
            <svg
              className="w-4 h-4 text-neutral-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Tag Filter */}
      <div className="flex-1">
        <label className="block text-sm font-medium text-neutral-600 dark:text-neutral-400 mb-2">
          {t('filterSort.filterByTag')}
        </label>
        <div className="relative">
          <select
            value={options.filterTag}
            onChange={(e) => handleTagFilterChange(e.target.value)}
            className="w-full px-4 py-3 pr-10 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 appearance-none"
          >
            <option value="">{t('filterSort.allTags')}</option>
            {availableTags.map((tag) => (
              <option key={tag} value={tag}>
                {tag}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
            <svg
              className="w-4 h-4 text-neutral-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterSortControls;
